export const FileStatus = Object.freeze({
  PENDING: 'pending',
  // Customs PDFs only: the backend accepted the file and an LLM extraction is
  // running in the background. Purely a *detection* state — it says nothing
  // about resolution and never maps onto `processingStatus` (D5).
  PARSING: 'parsing',
  RESOLVING: 'resolving',
  COMPLETED: 'completed',
  ERROR: 'error',
  SKIPPED: 'skipped',
});

/**
 * A finished hub file's status → its upload log's `processingStatus`, the one
 * spelling of both directions (the store reads it backwards on reopen).
 */
export const LOG_STATUS_BY_FILE_STATUS = Object.freeze({
  [FileStatus.COMPLETED]: 'success',
  [FileStatus.ERROR]: 'failed',
  [FileStatus.SKIPPED]: 'skipped',
});

export const TemplateType = Object.freeze({
  EFDA: 'efda',
  COMMS: 'comms',
  CG: 'cg',
  CUSTOMS: 'customs',
  // An HMRC Import item report. Detect answers it (UK declarants only) when a
  // file's header row matches the report, whatever the file's format.
  HMRC: 'hmrc',
  UNKNOWN: 'unknown',
});

// `UploadLog.detectionCache.customsExtraction.status` — the async-extraction
// contract the backend writes and the frontend polls (D4).
export const ExtractionStatus = Object.freeze({
  PENDING: 'pending',
  READY: 'ready',
  ERROR: 'error',
});

// Steps of the consolidated customs flow (D14). Review and creation are
// batch-level, so there is no per-file stepper any more: every parsed
// declaration in the batch feeds one grid, one supplier pass and one submit.
export const CustomsBatchStep = Object.freeze({
  REVIEW: 'review',
  SUPPLIERS: 'suppliers',
  // Optional and skippable (D20). A declaration never names an installation, so
  // this step offers the *resolved supplier's own* installations and nothing
  // else — there is no create path and no auto-selection at any score.
  INSTALLATIONS: 'installations',
  CONFIRM: 'confirm',
});

export const Phase = Object.freeze({
  DROPZONE: 'dropzone',
  ORGANIZE: 'organize',
  RESOLUTION: 'resolution',
  // Full-width batch review of every parsed customs declaration (D14). Entered
  // from RESOLUTION and exited back to it (mixed batches) or to SUMMARY.
  CUSTOMS_BATCH: 'customs_batch',
  SUMMARY: 'summary',
});

export const GroupType = Object.freeze({
  SAME_ALL: 'same_all',
  SAME_SUPPLIER: 'same_supplier',
});

export const ResolutionStep = Object.freeze({
  MATCH_SUPPLIER: 'match_supplier',
  MATCH_INSTALLATION: 'match_installation',
  PREVIEW_EMISSIONS: 'preview_emissions',
  UPLOAD: 'upload',
});

export const CgSheetStatus = Object.freeze({
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  SKIPPED: 'skipped',
});

export const BatchStatus = Object.freeze({
  OPEN: 'open',
  COMPLETED: 'completed',
  ABANDONED: 'abandoned',
});

export const UploadHubView = Object.freeze({
  UPLOAD: 'upload',
  HISTORY: 'history',
});

// A per-file ceiling, and deliberately no per-batch count: extraction is one
// job per file, so a bigger batch is more requests rather than a bigger one,
// and a customs run is however many declarations the quarter produced (D27).
export const MAX_FILE_SIZE_MB = 15;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
// What a file IS is detect's answer, per the declarant's regime — never the
// extension (upload-hub-regime-paths D2/D3). A PDF is a customs declaration.
export const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv', '.ods', '.pdf'];
export const ACCEPTED_EXTENSIONS_TEXT = '.xlsx, .xls, .csv, .ods and .pdf';

// A pending extraction older than this is treated as stranded (the backend was
// restarted mid-parse). Client-side guard; the server-side reaper (P5) is the
// authoritative fix. Five minutes (D71): the job's own work budget is two
// (backend `AI_JOB_WORK_BUDGET_MS`), so ten left a dead parse spinning for
// eight minutes past the point it could still finish.
export const STALE_EXTRACTION_MS = 5 * 60 * 1000;

// Past this a pending extraction is shown as slow — with the Retry the error
// state has — in the panel, the sidebar row and the rail (D71).
export const SLOW_EXTRACTION_MS = 75 * 1000;
